import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Coins, Check, X, Clock4, AlertCircle, ShieldCheck,
  Search, Filter, MessageSquare, Download, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));
const formatInt = (n) =>
  new Intl.NumberFormat('de-CH').format(Math.round(Number(n) || 0));
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const STATUS = {
  pending:   { label: 'In Prüfung',  cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',  Icon: Clock4 },
  confirmed: { label: 'Bestätigt',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: ShieldCheck },
  rejected:  { label: 'Abgelehnt',   cls: 'bg-red-500/15 text-red-300 border-red-500/30',         Icon: AlertCircle },
  cancelled: { label: 'Storniert',   cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30',     Icon: X },
};

const FILTERS = [
  { value: '',          label: 'Alle' },
  { value: 'pending',   label: 'In Prüfung' },
  { value: 'confirmed', label: 'Bestätigt' },
  { value: 'rejected',  label: 'Abgelehnt' },
  { value: 'cancelled', label: 'Storniert' },
];

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const DecisionDialog = ({ open, subscription, decision, onClose, onConfirm }) => {
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  if (!open || !subscription) return null;

  const labels = {
    confirmed: { title: 'Zeichnung bestätigen', cta: 'Bestätigen', cta_cls: 'bg-emerald-500 hover:bg-emerald-600' },
    rejected:  { title: 'Zeichnung ablehnen',   cta: 'Ablehnen',   cta_cls: 'bg-red-500 hover:bg-red-600' },
    cancelled: { title: 'Zeichnung stornieren', cta: 'Stornieren', cta_cls: 'bg-gray-500 hover:bg-gray-600' },
  };
  const meta = labels[decision] || labels.confirmed;

  const submit = async () => {
    setWorking(true);
    try {
      await onConfirm(subscription, decision, note);
      onClose();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="admin-sub-decision-dialog"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-[#111] border border-[#C4A55A]/40 rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <div className="mb-4">
          <div className="text-xs text-[#C4A55A] uppercase tracking-wider mb-1">Aktion</div>
          <h3 className="text-xl font-bold text-white">{meta.title}</h3>
          <p className="text-sm text-gray-400 mt-1">
            {subscription.investorName} · CHF {formatCHF(subscription.amountCHF)} ({formatInt(subscription.shareCount)} Aktien)
          </p>
        </div>
        <label className="block mb-4">
          <span className="text-sm text-gray-300 flex items-center gap-1">
            <MessageSquare className="h-3.5 w-3.5" /> Anmerkung (optional, geht in die Bestätigungs-Mail)
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none resize-none"
            data-testid="admin-sub-note"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-[#1a1a1a] border border-white/10 hover:bg-white/5 text-gray-200 text-sm"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={working}
            data-testid="admin-sub-submit"
            className={`px-5 py-2 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-50 ${meta.cta_cls}`}
          >
            {working ? 'Wird verarbeitet…' : meta.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditDialog = ({ open, subscription, onClose, onSaved }) => {
  const [form, setForm] = useState({
    shareCount: '', amountCHF: '', shareType: 'namen',
    referralCode: '', paymentMethod: 'bank', notes: '', adminNote: ''
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && subscription) {
      setError(null);
      setForm({
        shareCount: subscription.shareCount ?? '',
        amountCHF: subscription.amountCHF ?? '',
        shareType: subscription.shareType || 'namen',
        referralCode: subscription.referralCode || '',
        paymentMethod: subscription.paymentMethod || 'bank',
        notes: subscription.notes || '',
        adminNote: subscription.adminNote || '',
      });
    }
  }, [open, subscription]);

  if (!open || !subscription) return null;

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setWorking(true);
    try {
      const payload = {
        shareCount: form.shareCount === '' ? null : parseInt(form.shareCount, 10),
        amountCHF: form.amountCHF === '' ? null : parseFloat(form.amountCHF),
        shareType: form.shareType,
        referralCode: form.referralCode || '',
        paymentMethod: form.paymentMethod,
        notes: form.notes,
        adminNote: form.adminNote,
      };
      const r = await fetch(apiBase() + `/api/subscriptions/admin/${subscription.id}/edit`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Speichern fehlgeschlagen');
      toast.success('Zeichnung aktualisiert');
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="admin-sub-edit-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-[#111] border border-[#C4A55A]/40 rounded-2xl w-full max-w-xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs text-[#C4A55A] uppercase tracking-wider mb-1">Korrektur</div>
            <h3 className="text-xl font-bold text-white">Zeichnung bearbeiten</h3>
            <p className="text-sm text-gray-400 mt-1">
              {subscription.investorName} · {subscription.investorEmail}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="admin-sub-edit-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-gray-300">Anzahl Aktien</span>
              <input
                type="number" min="1"
                value={form.shareCount}
                onChange={(e) => setForm({ ...form, shareCount: e.target.value })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                data-testid="admin-sub-edit-count"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-300">Betrag CHF</span>
              <input
                type="number" min="0" step="100"
                value={form.amountCHF}
                onChange={(e) => setForm({ ...form, amountCHF: e.target.value })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                data-testid="admin-sub-edit-amount"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm text-gray-300">Aktientyp</span>
            <select
              value={form.shareType}
              onChange={(e) => setForm({ ...form, shareType: e.target.value })}
              className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
              data-testid="admin-sub-edit-type"
            >
              <option value="namen">Namenaktien</option>
              <option value="vorzugs">Vorzugsaktien</option>
            </select>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm text-gray-300">Vermittler-Code</span>
              <input
                type="text"
                value={form.referralCode}
                onChange={(e) => setForm({ ...form, referralCode: e.target.value.toUpperCase() })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
                placeholder="LH-XXXX"
              />
            </label>
            <label className="block">
              <span className="text-sm text-gray-300">Zahlungsart</span>
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
                className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none"
              >
                <option value="bank">Banküberweisung</option>
                <option value="other">Andere</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-sm text-gray-300">Anmerkung (intern)</span>
            <textarea
              value={form.adminNote}
              rows={2}
              onChange={(e) => setForm({ ...form, adminNote: e.target.value })}
              className="mt-1 w-full bg-black/60 border border-[#C4A55A]/30 rounded-lg px-3 py-2 text-white focus:border-[#C4A55A] focus:outline-none resize-none"
              data-testid="admin-sub-edit-adminnote"
            />
          </label>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-200 text-xs">
            Bei bestätigten Zeichnungen wird das Investitionsvolumen des Investors automatisch
            um die Differenz angepasst (Provisionsberechnung).
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[#1a1a1a] border border-white/10 hover:bg-white/5 text-gray-200 text-sm"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={working}
              data-testid="admin-sub-edit-save"
              className="px-5 py-2 rounded-lg bg-[#C4A55A] hover:bg-[#D4B56A] text-black font-semibold text-sm disabled:opacity-50"
            >
              {working ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const AdminSubscriptions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState({ open: false, sub: null, decision: 'confirmed' });
  const [editDialog, setEditDialog] = useState({ open: false, sub: null });

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
    }
  }, [user, navigate]);

  const fetchSubs = async (statusFilter = filter) => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const r = await fetch(apiBase() + '/api/subscriptions/admin' + qs, { credentials: 'include' });
      const data = await r.json();
      setSubs(data?.subscriptions || []);
    } catch {
      toast.error('Zeichnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubs(filter); }, [filter]);

  const filtered = subs.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (s.investorName || '').toLowerCase().includes(q) ||
      (s.investorEmail || '').toLowerCase().includes(q) ||
      (s.referralCode || '').toLowerCase().includes(q) ||
      String(s.id || '').includes(q)
    );
  });

  const totals = subs.reduce((acc, s) => {
    acc.count += 1;
    if (s.status === 'pending') acc.pending += 1;
    if (s.status === 'confirmed') acc.confirmedCHF += Number(s.amountCHF || 0);
    return acc;
  }, { count: 0, pending: 0, confirmedCHF: 0 });

  const submitDecision = async (sub, decision, note) => {
    try {
      const r = await fetch(apiBase() + `/api/subscriptions/admin/${sub.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: decision, adminNote: note || null })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Aktion fehlgeschlagen');
      toast.success({
        confirmed: 'Zeichnung bestätigt – Investor wurde benachrichtigt',
        rejected:  'Zeichnung abgelehnt – Investor wurde benachrichtigt',
        cancelled: 'Zeichnung storniert – Investor wurde benachrichtigt',
      }[decision] || 'Aktion erfolgreich');
      fetchSubs(filter);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteSubscription = async (sub) => {
    const confirmText = `Zeichnung von ${sub.investorName || sub.investorEmail} über CHF ${formatCHF(sub.amountCHF)} unwiderruflich löschen?`;
    if (!window.confirm(confirmText)) return;
    try {
      const r = await fetch(apiBase() + `/api/subscriptions/admin/${sub.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Löschen fehlgeschlagen');
      toast.success('Zeichnung gelöscht');
      fetchSubs(filter);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black text-white">
      <header className="bg-[#1a1a1a] border-b border-[#C4A55A]/20 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#C4A55A] hover:text-[#D4B56A] text-sm inline-flex items-center mb-1"
              data-testid="admin-subs-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zum Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-[#C4A55A]" />
              <h1 className="text-2xl font-bold">Zeichnungen verwalten</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8" data-testid="page-admin-subscriptions">
        {/* Workflow explanation */}
        <div className="mb-6 bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-[#C4A55A] mt-1 shrink-0" />
            <div className="text-sm text-gray-300 leading-relaxed">
              <p className="text-white font-semibold mb-1">Workflow</p>
              <p>
                Zeichnungen erscheinen mit Status <span className="text-amber-300 font-medium">„In Prüfung"</span>.
                Sobald die Banküberweisung eingegangen ist, klicken Sie auf <span className="text-emerald-300 font-medium">Bestätigen</span> –
                der Investor erhält automatisch eine E-Mail und das aktualisierte PDF, und die Aktien werden im Schweizer
                Aktienregister freigeschaltet. Mit <span className="text-red-300 font-medium">Ablehnen</span> oder
                <span className="text-gray-300 font-medium"> Stornieren</span> wird die Zeichnung aufgehoben und der Investor benachrichtigt.
              </p>
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm text-gray-400">Zeichnungen gesamt</CardTitle>
              <Coins className="h-4 w-4 text-[#C4A55A]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tabular-nums">{totals.count}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm text-gray-400">Offen</CardTitle>
              <Clock4 className="h-4 w-4 text-amber-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tabular-nums text-amber-300">{totals.pending}</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm text-gray-400">Bestätigt (CHF)</CardTitle>
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold tabular-nums text-emerald-300">{formatCHF(totals.confirmedCHF)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-1.5 text-gray-400 text-sm">
            <Filter className="h-4 w-4" /> Status:
          </div>
          {FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => setFilter(f.value)}
              data-testid={`admin-subs-filter-${f.value || 'all'}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.value
                  ? 'bg-[#C4A55A] text-black'
                  : 'bg-[#1a1a1a] border border-white/10 text-gray-200 hover:bg-white/5'
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, E-Mail, Code…"
              data-testid="admin-subs-search"
              className="bg-[#1a1a1a] border border-white/10 rounded-lg pl-9 pr-3 py-1.5 text-sm focus:border-[#C4A55A] focus:outline-none w-64"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-gray-400 text-sm p-6">Lade Zeichnungen…</div>
            ) : filtered.length === 0 ? (
              <div className="text-gray-400 text-sm p-6">Keine Zeichnungen gefunden.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-subs-table">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-white/10">
                      <th className="py-3 px-4 font-medium">Eingang</th>
                      <th className="py-3 px-4 font-medium">Investor</th>
                      <th className="py-3 px-4 font-medium">Aktientyp</th>
                      <th className="py-3 px-4 font-medium text-right">Aktien</th>
                      <th className="py-3 px-4 font-medium text-right">Betrag CHF</th>
                      <th className="py-3 px-4 font-medium">Vermittler</th>
                      <th className="py-3 px-4 font-medium">Status</th>
                      <th className="py-3 px-4 font-medium text-right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((s) => {
                      const meta = STATUS[s.status] || STATUS.pending;
                      const Icon = meta.Icon;
                      const isActionable = s.status === 'pending';
                      const isVorzugs = (s.shareType || 'namen') === 'vorzugs';
                      return (
                        <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`admin-sub-row-${s.id}`}>
                          <td className="py-3 px-4 whitespace-nowrap">{formatDate(s.createdAt)}</td>
                          <td className="py-3 px-4">
                            <div className="text-white">{s.investorName || '—'}</div>
                            <div className="text-xs text-gray-400">{s.investorEmail || '—'}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                                isVorzugs
                                  ? 'bg-purple-500/15 text-purple-200 border-purple-500/30'
                                  : 'bg-[#C4A55A]/15 text-[#C4A55A] border-[#C4A55A]/30'
                              }`}
                              data-testid={`admin-sub-type-${s.id}`}
                            >
                              {isVorzugs ? 'Vorzugs' : 'Namen'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right tabular-nums">{formatInt(s.shareCount)}</td>
                          <td className="py-3 px-4 text-right tabular-nums text-[#C4A55A]">{formatCHF(s.amountCHF)}</td>
                          <td className="py-3 px-4">{s.referralCode || '—'}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${meta.cls}`}>
                              <Icon className="h-3 w-3" />
                              {meta.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <a
                              href={apiBase() + `/api/subscriptions/${s.id}/pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[#C4A55A] hover:text-[#D4B56A] text-xs mr-3"
                              data-testid={`admin-sub-pdf-${s.id}`}
                            >
                              <Download className="h-3.5 w-3.5" /> PDF
                            </a>
                            {isActionable ? (
                              <div className="inline-flex gap-1 flex-wrap">
                                <Button
                                  size="sm"
                                  data-testid={`admin-sub-confirm-${s.id}`}
                                  onClick={() => setDialog({ open: true, sub: s, decision: 'confirmed' })}
                                  className="bg-emerald-500/20 hover:bg-emerald-500 text-emerald-200 hover:text-white text-xs h-8"
                                >
                                  <Check className="h-3.5 w-3.5 mr-1" /> Bestätigen
                                </Button>
                                <Button
                                  size="sm"
                                  data-testid={`admin-sub-reject-${s.id}`}
                                  onClick={() => setDialog({ open: true, sub: s, decision: 'rejected' })}
                                  className="bg-red-500/20 hover:bg-red-500 text-red-200 hover:text-white text-xs h-8"
                                >
                                  <X className="h-3.5 w-3.5 mr-1" /> Ablehnen
                                </Button>
                              </div>
                            ) : (
                              s.adminNote ? <span className="text-xs text-gray-400 italic mr-2">{s.adminNote}</span> : null
                            )}
                            <div className="inline-flex gap-1 ml-2">
                              <Button
                                size="sm"
                                data-testid={`admin-sub-edit-${s.id}`}
                                onClick={() => setEditDialog({ open: true, sub: s })}
                                className="bg-[#C4A55A]/15 hover:bg-[#C4A55A] text-[#C4A55A] hover:text-black text-xs h-8"
                                title="Korrektur / Bearbeiten"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                data-testid={`admin-sub-delete-${s.id}`}
                                onClick={() => deleteSubscription(s)}
                                className="bg-red-500/10 hover:bg-red-500 text-red-300 hover:text-white text-xs h-8"
                                title="Zeichnung löschen"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <DecisionDialog
        open={dialog.open}
        subscription={dialog.sub}
        decision={dialog.decision}
        onClose={() => setDialog({ open: false, sub: null, decision: 'confirmed' })}
        onConfirm={submitDecision}
      />

      <EditDialog
        open={editDialog.open}
        subscription={editDialog.sub}
        onClose={() => setEditDialog({ open: false, sub: null })}
        onSaved={() => fetchSubs(filter)}
      />
    </div>
  );
};
